<template>
<div class="flex min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-300">

  <!-- Sidebar -->
  <HrSidebar :isOpen="isSidebarOpen" />

  <!-- Main -->
  <main 
    class="flex-1 overflow-x-hidden transition-all duration-300"
    :class="{'ml-64': isSidebarOpen, 'ml-0': !isSidebarOpen}"
  >

  <!-- Navbar -->
  <header class="fixed top-0 z-50 w-full bg-white dark:bg-gray-800 shadow-sm">
    <HrTopbar
      :page-title="currentPageTitle"
      :is-sidebar-open="isSidebarOpen"
      :is-dark-mode="isDarkMode"
      @toggle-sidebar="toggleSidebar"
      @toggle-dark-mode="toggleDarkMode"
    />
  </header>

  <!-- Page Content -->
  <div class="p-6 mt-16">

    <!-- Top Bar -->
    <div class="flex justify-between items-center mb-8 p-4">

      <input
        v-model="search"
        type="text"
        placeholder="Search department..."
        class="border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2 w-72  focus:outline-none focus:ring-2 focus:ring-secondary dark:bg-gray-700 dark:text-white"
      />

      <button
        @click="openCreate"
        class="bg-[#74bd54] text-white px-5 py-2 rounded-lg hover:bg-[#63a43f] shadow"
      >
        + Add Department
      </button>

    </div>

    <!-- Department Grid -->
    <div
      v-if="filteredDepartments.length"
      class="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6"
    >

      <div
        v-for="dept in filteredDepartments"
        :key="dept.id"
        class="bg-white dark:bg-gray-800 p-6 rounded-xl shadow hover:shadow-xl transition group"
      >

        <!-- Card Header -->
        <div class="flex justify-between items-start mb-4">

          <div class="flex items-center space-x-3">

            <div class="w-12 h-12 rounded-full bg-secondary flex items-center justify-center text-white font-bold text-lg">
              {{ dept.code }}
            </div>

            <div>
              <h3 class="font-semibold text-gray-800 dark:text-white">
                {{ dept.name }}
              </h3>

              <span class="text-xs text-gray-500">
                {{ dept.staff }} staff
              </span>
            </div>

          </div>

        </div>

        <!-- Description -->
        <p class="text-sm text-gray-500 dark:text-gray-400 mb-6">
          {{ dept.description }}
        </p>

        <!-- Actions -->
        <div class="flex justify-between items-center text-sm">

          <button
            @click="editDepartment(dept)"
            class="text-blue-600 hover:underline"
          >
            <i class="fas fa-edit"></i>
            Edit
          </button>

          <button
            @click="deleteDepartment(dept.id)"
            class="text-red-500 hover:underline"
          >
            <i class="fas fa-trash"></i>
            Delete
          </button>

        </div>

      </div>

    </div>

    <!-- Empty State -->
    <div
      v-else
      class="text-center text-gray-400 mt-20"
    >
      No departments yet
    </div>


    <!-- Modal -->
    <Transition name="modal">
    <div
      v-if="showModal"
      class="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
    >

      <div class="bg-white dark:bg-gray-800 rounded-xl p-6 w-[420px] shadow-lg">

        <h3 class="text-lg font-semibold text-[#035a76] mb-6">
          {{ editing ? 'Edit Department' : 'Create Department' }}
        </h3>

        <form @submit.prevent="saveDepartment" class="space-y-4">

          <input
            v-model="form.name"
            type="text"
            placeholder="Department Name"
            required
            class="w-full border border-gray-300 dark:border-gray-600 rounded p-2 dark:bg-gray-700 dark:text-white"
          />

          <input
            v-model="form.code"
            type="text"
            placeholder="Code (HR, FIN, IT...)"
            required
            class="w-full border border-gray-300 dark:border-gray-600 rounded p-2 dark:bg-gray-700 dark:text-white"
          />

          <!-- <input
            v-model="form.staff"
            type="number"
            placeholder="Staff count"
            class="w-full border border-gray-300 dark:border-gray-600 rounded p-2 dark:bg-gray-700 dark:text-white"
          /> -->

          <textarea
            v-model="form.description"
            placeholder="Description"
            class="w-full border border-gray-300 dark:border-gray-600 rounded p-2 dark:bg-gray-700 dark:text-white"
          ></textarea>

          <div class="flex justify-end space-x-3 pt-2">

            <button
              type="button"
              @click="closeModal"
              class="px-4 py-2 bg-gray-300 rounded"
            >
              Cancel
            </button>

            <button
              type="submit"
              class="px-4 py-2 bg-[#74bd54] text-white rounded hover:bg-[#63a43f]"
            >
              Save
            </button>

          </div>

        </form>

      </div>

    </div>
    </Transition>

  </div>

  </main>

</div>
</template>

<script setup>

import { ref, computed, onMounted } from "vue"
import api from "@/services/api"
import HrSidebar from "../../components/hr/HrSidebar.vue"
import HrTopbar from "../../components/hr/HrTopbar.vue"

const currentPageTitle = ref("Departments")

const isSidebarOpen = ref(true)
const isDarkMode = ref(false)

const showModal = ref(false)
const editing = ref(false)

const search = ref("")

const departments = ref([])

// const departments = ref([
// {
// id:1,
// name:"Human Resources",
// code:"HR",
// description:"Handles recruitment and employee relations",
// staff:12
// },
// {
// id:2,
// name:"Finance",
// code:"FIN",
// description:"Company financial management",
// staff:8
// },
// {
// id:3,
// name:"Information Technology",
// code:"IT",
// description:"System infrastructure and support",
// staff:6
// }
// ])

// const form = ref({
// id:null,
// name:"",
// code:"",
// description:"",
// staff:0
// })

const fetchDepartments = async () => {
  try{
    const response = await api.get("Departments.php");
    departments.value = response.data.map(department => ({
      id:department.id,
      name:department.name,
      code:department.code,
      description:department.description
    }));

  } catch (error) {
    console.error("Error fetching Departments:", error);
  }
};

const form = ref({ id:null, name:"", code:"", description:""});


const filteredDepartments = computed(() => {
return departments.value.filter(d =>
d.name.toLowerCase().includes(search.value.toLowerCase())
)
})

const openCreate = () => {
editing.value = false
form.value = {id:null,name:"",code:"",description:"",staff:0}
showModal.value = true
}

const editDepartment = (dept) => {
editing.value = true
form.value = {...dept}
showModal.value = true
}

// const saveDepartment = () => {

// if(editing.value){

// const index = departments.value.findIndex(d => d.id === form.value.id)
// departments.value[index] = {...form.value}

// }else{

// departments.value.push({
// id:Date.now(),
// ...form.value
// })

// }

// closeModal()

// }

const saveDepartment = async () =>{
  try{
  let response;

  if(editing.value){
    response = await api.put("Departments.php", form.value)
    const index = departments.value.findIndex(d => d.id === form.value.id)
      if (index !== -1) {
        departments.value[index] = { ...form.value };
      }
      
  }else{
        response = await api.post("Departments.php", form.value)
        departments.value.push({
          id:response.data.id,
          name:response.data.name,
          code:response.data.code,
          description:response.data.description
        });
      }
closeModal();

  } catch (error) {
    console.error("Error saving department:", error);
  }
};


const deleteDepartment = async (id) => {
try {

    await api.delete("Departments.php", {
      data: { id: id }
    });

    departments.value = departments.value.filter(u => u.id !== id);

  } catch (error) {
    console.error("Error deleting department:", error);
  }
}

const closeModal = () => {
showModal.value = false
}

const toggleSidebar = () => {
isSidebarOpen.value = !isSidebarOpen.value
}

const toggleDarkMode = () => {

isDarkMode.value = !isDarkMode.value

if(isDarkMode.value){
document.documentElement.classList.add("dark")
}else{
document.documentElement.classList.remove("dark")
}

}
onMounted(() =>{
  fetchDepartments();
});

</script>

<style>

.modal-enter-active,
.modal-leave-active{
transition: all .25s ease;
}

.modal-enter-from{
opacity:0;
transform:scale(.9);
}

.modal-leave-to{
opacity:0;
transform:scale(.9);
}

</style>